import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FileEndpoints } from '@apis/cas';
import { FileMeta } from '@apis/cas/generated';
import { AuthService } from '@core/auth.service';
import { db, FileDb } from '@core/database/db';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LocalFileMetaProvider } from './LocalFileMetaProvider';

export type AssetReaderState = 'Initializing' | 'Fetching' | 'FullyCached' | 'Error';

/**
 * Service for reading assets, providing access to file URLs and handling local caching.
 * It initializes by fetching all file metadata and prefetching files for local access.
 */
@Injectable({
  providedIn: 'root',
})
export class AssetReaderService {
  // Injecting the correct one because angular complains if you do it with an injection token in app.config
  #fileEndpoints = environment.useOfflineData ? inject(LocalFileMetaProvider) : inject(FileEndpoints);
  state = signal<AssetReaderState>('Initializing');
  #stateObservable = toObservable(this.state);
  #preDatabaseCache: Map<string, FileMeta> = new Map<string, FileMeta>();
  #auth = inject(AuthService);

  constructor() {
    this.#fileEndpoints.getAllFileMetas().subscribe({
      next: async (fileMetaCollection) => {
        await this.#auth.waitForLogin();
        //wait until we're authenticated in authservice
        if (this.#auth.state() !== 'LOGGED_IN') {
          console.log("Can't prefetch files because we are not logged in.");
          return;
        }
        const files = fileMetaCollection.files;
        // map the file metas to a map for easier access
        files.forEach((fileMeta) => {
          this.#preDatabaseCache.set(fileMeta.uid, fileMeta);
        });
        this.state.set('Fetching'); // we're ready because we can access the file urls before the database is ready

        // Handle missing files in the database
        const ids = files.map((fileMeta) => fileMeta.uid);
        const bulkGetResult = (await db.files.bulkGet(ids)).filter((file) => file !== undefined);
        const missingFiles = files.filter((_, index) => {
          return bulkGetResult[index] === undefined;
        });
        const batchSize = 50;
        // Process missing files in batches of 50
        for (let i = 0; i < missingFiles.length; i += batchSize) {
          const batch = missingFiles.slice(i, i + batchSize).map((fileMeta) => ({
            id: fileMeta.uid,
            modified: fileMeta.modified,
            url: this.removeTokenFromUrl(fileMeta.url),
            localCopy: undefined,
            requiresFetch: 1,
          }));
          await db.files.bulkPut(batch);
        }

        // Handle outdated files in the database
        const outdatedFiles = files.filter((fileMeta, index) => {
          const existingFile = bulkGetResult[index];
          if (existingFile === undefined) return false;
          // TODO: Check if this is sufficient for all cases
          return existingFile.modified !== fileMeta.modified;
        });

        // Process outdated files in batches of 50
        for (let i = 0; i < outdatedFiles.length; i += batchSize) {
          const batch = outdatedFiles.slice(i, i + batchSize).map((fileMeta) => ({
            key: fileMeta.uid,
            changes: {
              modified: fileMeta.modified,
              url: this.removeTokenFromUrl(fileMeta.url),
              requiresFetch: 1,
            },
          }));
          await db.files.bulkUpdate(batch);
        }
        console.log('Outdated file metas updated in database:', outdatedFiles.length);
        console.log('Missing file metas added to database:', missingFiles.length);
        const accessToken = await this.#auth.getAccessToken();
        //check if any entry has requiresFetch === 1
        const requiresFetch = await db.files.where('requiresFetch').equals(1).count();
        console.log(`Files that require fetching: ${requiresFetch}`);
        if (requiresFetch === 0) {
          this.state.set('FullyCached');
        } else if (accessToken) {
          this.prefetchFiles(accessToken);
        }
      },
      error: (error) => {
        console.error('Error loading file metas:', error);
        this.state.set('Error');
      },
    });
  }

  /**
   * Waits for the asset reader to finish initializing.
   *
   * @returns A promise that resolves when the asset reader's state is no longer 'Initializing'.
   */
  private async waitForInitialization() {
    await firstValueFrom(this.#stateObservable.pipe(filter((state) => state !== 'Initializing')));
  }

  /**
   * Retrieves the URL of a file based on its unique identifier (UID).
   * If the file is available locally, the local URL is returned.
   * Otherwise, if the file is not available locally, the online URL from the stored metadata is returned.
   *
   * @param   uid Unique identifier of the file.
   * @returns     The URL of the file, or throws an error if not found.
   */
  async getFileUrl(uid: string) {
    await this.waitForInitialization();
    const dbEntry = await db.files.get(uid);
    if (dbEntry === undefined || dbEntry.localCopy === undefined) {
      const fallback = this.#preDatabaseCache.get(uid)?.url;
      if (fallback === undefined) throw new Error(`File ${uid} not found in database or pre-cache`);
      return fallback;
    }
    const blobUrl = URL.createObjectURL(dbEntry.localCopy.blob);
    return blobUrl;
  }

  /**
   * Retrieves the URL of the local copy of a file based on its unique identifier (UID).
   * Throws an error if the local copy is not available in the database.
   *
   * @param   uid Unique identifier of the file.
   * @returns     The local blob URL of the file.
   */
  async getLocalFileUrl(uid: string) {
    await this.waitForInitialization();
    const dbEntry = await db.files.get(uid);
    if (dbEntry && dbEntry.localCopy) {
      return URL.createObjectURL(dbEntry.localCopy.blob);
    }
    throw new Error(`Local copy for file ${uid} not found`);
  }

  /**
   * Retrieves the server URL of a file based on its unique identifier (UID).
   * It returns the online URL provided from the stored metadata.
   *
   * @param   uid Unique identifier of the file.
   * @returns     The online URL of the file.
   */
  async getServerFileUrl(uid: string) {
    await this.waitForInitialization();
    const serverUrl = this.#preDatabaseCache.get(uid)?.url;
    if (serverUrl === undefined) throw new Error(`File ${uid} not found in database or pre-cache`);
    return serverUrl;
  }

  async getFileMeta(uid: string): Promise<FileMeta | undefined> {
    await this.waitForInitialization();
    const dbEntry = await db.files.get(uid);
    const meta = this.#preDatabaseCache.get(uid);
    if (dbEntry === undefined) {
      if (meta === undefined) throw new Error(`File ${uid} not found in database or pre-cache`);
      return meta;
    }
    if (dbEntry.localCopy && meta) {
      const localUrl = URL.createObjectURL(dbEntry.localCopy.blob);
      return { ...meta, url: localUrl };
    }
    return meta;
  }

  private prefetchFiles(accessToken: string) {
    const filesToFetch = db.files.where('requiresFetch').equals(1).toArray();
    const startTime = performance.now();
    console.log('Prefetching files started...');
    // Start a background queue processor and log when finished.
    void this.processQueue(filesToFetch, accessToken).then(() => {
      const duration = performance.now() - startTime;
      console.log(`Prefetching files completed in ${duration.toFixed(2)} ms.`);
      this.state.set('FullyCached');
    });
  }

  private async processQueue(filesToFetchPromise: Promise<FileDb[]>, accessToken: string) {
    const filesToFetch = await filesToFetchPromise;
    const concurrency = 2; // Max concurrent requests
    let active = 0;
    let index = 0;

    return new Promise<void>((resolve) => {
      if (filesToFetch.length === 0) {
        resolve();
        return;
      }
      const processNext = async () => {
        if (index >= filesToFetch.length) {
          if (active === 0) {
            resolve();
          }
          return;
        }

        active++;
        const file = filesToFetch[index++];

        try {
          const response = await fetch(file.url, {
            priority: 'low',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          const blob = await response.blob();
          await db.files.update(file.id, { localCopy: { blob }, requiresFetch: 0 });
        } catch (error) {
          console.error(`Error prefetching file ${file?.id}:`, error);
        }

        active--;
        processNext();
      };

      // Start initial batch of workers
      for (let i = 0; i < Math.min(concurrency, filesToFetch.length); i++) {
        processNext();
      }
    });
  }

  removeTokenFromUrl(url: string): string {
    const tokenIndex = url.indexOf('?token=');
    if (tokenIndex !== -1) {
      return url.substring(0, tokenIndex);
    }
    return url;
  }
}
